<template>
    <section class="dp-layout psychology-appointment-page">
        <!-- 左侧组织架构树 -->
        <section class="dp-left">
            <DepartmentPane directory-scope="teaching" pane-title="组织架构" @tree-node-click="treeNodeClick"></DepartmentPane>
        </section>

        <!-- 右侧预约记录主区 -->
        <section class="dp-right">
            <template v-if="hasSchoolSelected">
                <!-- 顶部检索工具栏 -->
                <div class="filter-bar">
                        <el-form :inline="true" size="small" class="filter-form">
                            <el-form-item label="预约状态">
                                <el-select v-model="filterForm.status" placeholder="全部状态" clearable style="width: 130px;" @change="handleSearch">
                                    <el-option label="待审核" :value="0" />
                                    <el-option label="已同意" :value="1" />
                                    <el-option label="已拒绝" :value="2" />
                                    <el-option label="已签到" :value="3" />
                                    <el-option label="已取消" :value="4" />
                                </el-select>
                            </el-form-item>
                            <el-form-item label="日期范围">
                                <el-date-picker
                                    v-model="dateRange"
                                    type="daterange"
                                    start-placeholder="开始日期"
                                    end-placeholder="结束日期"
                                    value-format="yyyy-MM-dd"
                                    style="width: 240px;"
                                    @change="handleSearch"
                                />
                            </el-form-item>
                            <el-form-item label="关键字">
                                <el-input v-model="filterForm.keyword" placeholder="学生姓名或发起人" clearable @keyup.enter.native="handleSearch" style="width: 160px;" />
                            </el-form-item>
                            <el-form-item>
                                <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
                                <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
                            </el-form-item>
                        </el-form>

                        <div class="action-btn-group">
                            <el-button type="success" size="small" icon="el-icon-download" :loading="exporting" @click="handleExport">
                                导出 Excel
                            </el-button>
                        </div>
                    </div>

                    <!-- 数据表格 -->
                    <div class="tbl-scroll-host">
                        <el-table :data="tableData" v-loading="loading" border style="width: 100%;" height="100%" class="compact-table">
                        <el-table-column type="index" label="序号" width="55" align="center" />
                        <el-table-column prop="StudentName" label="学生姓名" width="120">
                            <template slot-scope="scope">
                                <span class="student-name">{{ scope.row.StudentName }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="CampusName" label="所属校区" width="120" show-overflow-tooltip>
                            <template slot-scope="scope">
                                <el-tag v-if="scope.row.CampusName && scope.row.CampusName !== '-'" size="mini" type="info">{{ scope.row.CampusName }}</el-tag>
                                <span v-else>-</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="DepartmentName" label="所在班级" min-width="140" show-overflow-tooltip>
                            <template slot-scope="scope">
                                <span>{{ scope.row.DepartmentName || scope.row.ClassName || '-' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="AppointDate" label="预约日期" width="110" align="center">
                            <template slot-scope="scope">
                                <span>{{ formatDate(scope.row.AppointDate) }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="预约时段 / 时间" min-width="170">
                            <template slot-scope="scope">
                                <div v-if="scope.row.TimeMode === 1">
                                    <el-tag size="mini" type="info">特殊课程</el-tag>
                                    <span style="margin-left: 6px;">{{ scope.row.SpecialCourseTimeName || '联动时段' }}</span>
                                </div>
                                <div v-else>
                                    <el-tag size="mini">自选</el-tag>
                                    <span style="margin-left: 6px;">{{ scope.row.StartTime || scope.row.CustomStartTime }} ~ {{ scope.row.EndTime || scope.row.CustomEndTime }}</span>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column prop="CreatedTeacherName" label="发起班主任" width="110">
                            <template slot-scope="scope">
                                <span>{{ scope.row.ApplyTeacherName || scope.row.CreatedTeacherName || '-' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="ConsultReason" label="预约咨询理由" min-width="220" show-overflow-tooltip>
                            <template slot-scope="scope">
                                <span>{{ scope.row.Reason || scope.row.ConsultReason || '-' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="Status" label="状态" width="100" align="center">
                            <template slot-scope="scope">
                                <el-tag
                                    :type="getAppointmentStatusTagType(scope.row.Status)"
                                    :effect="Number(scope.row.Status) === 3 ? 'dark' : 'plain'"
                                    size="small"
                                >
                                    {{ formatAppointmentStatusText(scope.row.Status) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="AuditTeacherName" label="审核心理老师" width="110">
                            <template slot-scope="scope">
                                <span>{{ scope.row.AuditTeacherName || '-' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="CheckInTime" label="核验签到时间" width="150" align="center">
                            <template slot-scope="scope">
                                <span>{{ scope.row.CheckInTime || '-' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="考勤联动状态" width="130" align="center">
                            <template slot-scope="scope">
                                <el-tag v-if="scope.row.RollCallSynced || scope.row.RollCallSyncStatus === 1" type="success" size="mini" :title="scope.row.RollCallSyncRemark || '已同步特殊课程考勤为其他(心理咨询)'">
                                    已同步为其他
                                </el-tag>
                                <span v-else class="text-muted" :title="scope.row.RollCallSyncRemark || '未联动考勤'">
                                    {{ scope.row.RollCallSyncRemark ? (scope.row.RollCallSyncRemark.length > 8 ? scope.row.RollCallSyncRemark.slice(0, 8) + '...' : scope.row.RollCallSyncRemark) : '-' }}
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column label="咨询纪要" width="120" align="center">
                            <template slot-scope="scope">
                                <el-button
                                    v-if="scope.row.HasCounselingRecord || (scope.row.CounselingRecords && scope.row.CounselingRecords.length > 0)"
                                    type="text"
                                    size="mini"
                                    @click="viewDetail(scope.row)"
                                    title="点击查看本次预约咨询纪要"
                                >
                                    <el-tag type="success" size="mini" effect="plain" class="clickable-tag">
                                        已记录 ({{ (scope.row.CounselingRecords && scope.row.CounselingRecords.length) || 1 }}条)
                                    </el-tag>
                                </el-button>
                                <el-button
                                    v-else-if="Number(scope.row.Status) === 3"
                                    type="text"
                                    size="mini"
                                    @click="openAddCounseling(scope.row)"
                                    title="现场已签到，点击立即录入咨询纪要"
                                >
                                    <el-tag type="warning" size="mini" effect="plain" class="clickable-tag">
                                        待登记
                                    </el-tag>
                                </el-button>
                                <span v-else class="text-muted">-</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="200" align="center">
                            <template slot-scope="scope">
                                <div class="table-op-pair">
                                    <el-button type="text" class="table-op-link" @click="viewDetail(scope.row)">详情</el-button>
                                    <el-button
                                        v-if="Number(scope.row.Status) === 3 && (!scope.row.CounselingRecords || scope.row.CounselingRecords.length === 0)"
                                        type="text"
                                        class="table-op-link"
                                        style="color: #E6A23C;"
                                        @click="openAddCounseling(scope.row)"
                                    >
                                        录入纪要
                                    </el-button>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>
                    </div>

                    <!-- 分页 -->
                    <el-col :span="24" class="toolbar">
                        <el-pagination
                            background
                            layout="total, sizes, prev, pager, next, jumper"
                            :current-page="page"
                            :page-sizes="[10, 20, 50, 100]"
                            :page-size="pageSize"
                            :total="total"
                            @size-change="handleSizeChange"
                            @current-change="handlePageChange"
                            style="float: right;"
                        />
                    </el-col>
                </template>

                <div v-else class="no-department-tip">
                    <div class="no-department-tip__inner">
                        <i class="el-icon-date"></i>
                        <p>请在左侧选择所属组织或班级，查看预约流水记录</p>
                    </div>
                </div>
            </section>

            <!-- 详情弹窗 -->
            <el-dialog title="心理咨询预约明细详情" :visible.sync="detailDialogVisible" width="680px" top="6vh">
                <div v-if="currentRecord" class="appointment-detail">
                    <el-descriptions :column="2" border size="small">
                        <el-descriptions-item label="学生姓名">{{ currentRecord.StudentName }}</el-descriptions-item>
                        <el-descriptions-item label="所在班级">{{ currentRecord.DepartmentName || currentRecord.ClassName || '-' }}</el-descriptions-item>
                        <el-descriptions-item label="预约日期">{{ formatDate(currentRecord.AppointDate) }}</el-descriptions-item>
                        <el-descriptions-item label="时段模式">
                            {{ currentRecord.TimeMode === 1 ? '特殊课程时段' : '自选时间' }}
                        </el-descriptions-item>
                        <el-descriptions-item label="预约时段 / 时间" :span="2">
                            <span v-if="currentRecord.TimeMode === 1">{{ currentRecord.SpecialCourseTimeName || '特殊课程联动时段' }}</span>
                            <span v-else>{{ currentRecord.StartTime || currentRecord.CustomStartTime }} ~ {{ currentRecord.EndTime || currentRecord.CustomEndTime }}</span>
                        </el-descriptions-item>
                        <el-descriptions-item label="发起班主任">{{ currentRecord.ApplyTeacherName || currentRecord.CreatedTeacherName || '-' }}</el-descriptions-item>
                        <el-descriptions-item label="发起时间">{{ currentRecord.CreateTime }}</el-descriptions-item>
                        <el-descriptions-item label="当前状态" :span="2">
                            <el-tag
                                :type="getAppointmentStatusTagType(currentRecord.Status)"
                                :effect="Number(currentRecord.Status) === 3 ? 'dark' : 'plain'"
                                size="small"
                            >
                                {{ formatAppointmentStatusText(currentRecord.Status) }}
                            </el-tag>
                        </el-descriptions-item>
                        <el-descriptions-item label="咨询预约理由" :span="2">
                            {{ currentRecord.Reason || currentRecord.ConsultReason || '未填写' }}
                        </el-descriptions-item>
                        <el-descriptions-item label="审核老师">{{ currentRecord.AuditTeacherName || '-' }}</el-descriptions-item>
                        <el-descriptions-item label="审核时间">{{ currentRecord.AuditTime || '-' }}</el-descriptions-item>
                        <el-descriptions-item label="审核意见 / 理由" :span="2">
                            {{ currentRecord.AuditRemark || '-' }}
                        </el-descriptions-item>
                        <el-descriptions-item label="核验签到时间">{{ currentRecord.CheckInTime || '未签到' }}</el-descriptions-item>
                        <el-descriptions-item label="考勤联动状态">
                            {{ (currentRecord.RollCallSynced || currentRecord.RollCallSyncStatus === 1) ? ('已同步（' + (currentRecord.RollCallSyncRemark || '考勤状态修改为其他，备注心理咨询') + '）') : (currentRecord.RollCallSyncRemark || '未同步/不联动') }}
                        </el-descriptions-item>
                    </el-descriptions>

                    <!-- 人脸核验照片展示（如有）；OSS 私有桶用 oss-el-image。最后修改：2026-09-14 -->
                    <div v-if="currentRecord.CheckInPhotoUrl" class="face-photo-box">
                        <div class="photo-title">人脸比对核验抓拍：</div>
                        <oss-el-image
                            :src="currentRecord.CheckInPhotoUrl"
                            :preview-src-list="[currentRecord.CheckInPhotoUrl]"
                            style="width: 140px; height: 140px; border-radius: 6px; border: 1px solid #e2e8f0;"
                            fit="cover"
                        />
                    </div>

                    <!-- 本次预约关联纪要 -->
                    <div class="appointment-counseling-box">
                        <div class="counseling-box-header">
                            <div class="c-header-left">
                                <i class="el-icon-chat-dot-round"></i>
                                <span class="box-title">本次预约关联纪要</span>
                                <el-tag
                                    v-if="currentRecord.CounselingRecords && currentRecord.CounselingRecords.length > 0"
                                    size="mini"
                                    type="success"
                                    effect="plain"
                                    style="margin-left: 8px;"
                                >
                                    已登记 {{ currentRecord.CounselingRecords.length }} 条
                                </el-tag>
                                <el-tag
                                    v-else-if="Number(currentRecord.Status) === 3"
                                    size="mini"
                                    type="warning"
                                    effect="plain"
                                    style="margin-left: 8px;"
                                >
                                    已签到 · 待登记
                                </el-tag>
                                <el-tag v-else size="mini" type="info" effect="plain" style="margin-left: 8px;">
                                    {{ formatAppointmentStatusText(currentRecord.Status) }}
                                </el-tag>
                            </div>
                            <div class="c-header-right" v-if="Number(currentRecord.Status) === 3">
                                <el-button
                                    v-if="currentRecord.CounselingRecords && currentRecord.CounselingRecords.length > 0"
                                    type="primary"
                                    plain
                                    size="mini"
                                    icon="el-icon-plus"
                                    @click="openAddCounseling(currentRecord)"
                                >
                                    补充纪要
                                </el-button>
                            </div>
                        </div>

                        <!-- 场景 1：已录入纪要（1条或多条） -->
                        <div v-if="currentRecord.CounselingRecords && currentRecord.CounselingRecords.length > 0" class="counseling-record-list">
                            <div v-for="(cItem, cIdx) in currentRecord.CounselingRecords" :key="cItem.Id" class="counseling-record-card">
                                <div class="c-card-header">
                                    <div class="c-teacher">
                                        <span v-if="currentRecord.CounselingRecords.length > 1" class="c-seq-badge">
                                            #{{ currentRecord.CounselingRecords.length - cIdx }}
                                        </span>
                                        <i class="el-icon-s-custom"></i>
                                        <span>咨询老师：{{ cItem.TeacherName || '心理咨询师' }}</span>
                                        <el-tag
                                            v-if="cItem.AttentionLevel !== null && cItem.AttentionLevel !== undefined"
                                            size="mini"
                                            :type="getAttentionTagType(cItem.AttentionLevel)"
                                            style="margin-left: 8px;"
                                        >
                                            {{ formatAttentionText(cItem.AttentionLevel) }}
                                        </el-tag>
                                        <el-tag
                                            v-if="cIdx === 0 && currentRecord.CounselingRecords.length > 1"
                                            size="mini"
                                            type="info"
                                            effect="plain"
                                            style="margin-left: 6px;"
                                        >
                                            最新
                                        </el-tag>
                                    </div>
                                    <div class="c-time">
                                        <i class="el-icon-time"></i> {{ formatRecordTime(cItem) }}
                                    </div>
                                </div>
                                <div class="c-content">{{ cItem.Content }}</div>
                                <div v-if="cItem.Attachments && cItem.Attachments.length > 0" class="c-attachments">
                                    <span class="att-title">佐证附件：</span>
                                    <psychology-attachment-viewer
                                        :attachments="cItem.Attachments"
                                        :school-id="schoolId"
                                        layout="compact"
                                        image-size="52px"
                                        style="flex: 1;"
                                    />
                                </div>
                                <div v-if="cItem.NextFollowUpDate || cItem.Remark" class="c-footer">
                                    <span v-if="cItem.NextFollowUpDate" class="follow-date">
                                        <i class="el-icon-date"></i> 下次跟进日期：{{ formatDate(cItem.NextFollowUpDate) }}
                                    </span>
                                    <span v-if="cItem.Remark" class="remark">
                                        <i class="el-icon-notebook-2"></i> 备注：{{ cItem.Remark }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- 场景 2：已签到，但心理老师还没写纪要 -->
                        <div v-else-if="Number(currentRecord.Status) === 3" class="counseling-empty-action-box">
                            <div class="empty-action-tip">
                                <i class="el-icon-warning"></i>
                                <span>学生已于 <strong>{{ currentRecord.CheckInTime || '当日' }}</strong> 完成现场签到，心理老师暂未登记咨询纪要。</span>
                            </div>
                            <el-button type="primary" size="small" icon="el-icon-edit-outline" @click="openAddCounseling(currentRecord)">
                                录入纪要
                            </el-button>
                        </div>

                        <!-- 场景 3：未签到 / 待审核 / 已拒绝 / 已取消 -->
                        <div v-else class="counseling-empty-tip is-muted">
                            <i class="el-icon-info"></i>
                            <span>预约尚未核验签到，暂无辅导纪要</span>
                        </div>
                    </div>
                </div>
                <div slot="footer" class="dialog-footer">
                    <el-button type="primary" size="small" @click="detailDialogVisible = false">关 闭</el-button>
                </div>
            </el-dialog>

            <!-- 录入/补充咨询纪要弹窗 -->
            <el-dialog
                :title="counselingDialogTitle"
                :visible.sync="counselingDialogVisible"
                width="560px"
                append-to-body
                :close-on-click-modal="false"
            >
                <el-form :model="counselingForm" :rules="counselingRules" ref="counselingForm" label-width="110px" size="small">
                    <el-form-item label="学生姓名">
                        <span style="font-weight: 600; color: #303133;">{{ counselingForm.StudentName }}</span>
                        <span v-if="counselingForm.DepartmentName" style="color: #909399; margin-left: 8px;">
                            ({{ counselingForm.DepartmentName }})
                        </span>
                    </el-form-item>
                    <el-form-item label="关联预约">
                        <el-tag type="primary" effect="plain" size="small">
                            {{ formatDate(counselingForm.AppointDate) }} · {{ counselingForm.AppointmentReason || '心理咨询预约' }}
                        </el-tag>
                    </el-form-item>
                    <el-form-item label="咨询开展时间" prop="CounselingDate">
                        <el-date-picker
                            v-model="counselingForm.CounselingDate"
                            type="datetime"
                            format="yyyy-MM-dd HH:mm"
                            value-format="yyyy-MM-dd HH:mm:ss"
                            placeholder="选择咨询开展时间"
                            style="width: 100%;"
                        />
                    </el-form-item>
                    <el-form-item label="咨询老师姓名">
                        <el-input v-model="counselingForm.TeacherName" placeholder="如：心理咨询师（留空将自动记录）" />
                    </el-form-item>
                    <el-form-item label="调整后关注等级">
                        <el-select v-model="counselingForm.AttentionLevel" placeholder="请选择评估后的关注等级（可选）" clearable style="width: 100%;">
                            <el-option label="正常 / 一般" :value="0" />
                            <el-option label="关注" :value="1" />
                            <el-option label="重点关注" :value="2" />
                            <el-option label="危机干预" :value="3" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="本次咨询纪要" prop="Content">
                        <el-input
                            v-model="counselingForm.Content"
                            type="textarea"
                            :rows="5"
                            placeholder="请客观如实记录咨询会谈情况、学生表达内容、心理评估观察及后续辅导建议..."
                            maxlength="2000"
                            show-word-limit
                        />
                    </el-form-item>
                    <el-form-item label="建议下次跟进">
                        <el-date-picker v-model="counselingForm.NextFollowUpDate" type="date" value-format="yyyy-MM-dd" placeholder="建议下次会谈跟进日期（可选）" style="width: 100%;" />
                    </el-form-item>
                    <el-form-item label="补充备注">
                        <el-input v-model="counselingForm.Remark" placeholder="其他需要留存的跟进备忘（可选）" />
                    </el-form-item>
                </el-form>
                <div slot="footer" class="dialog-footer">
                    <el-button size="small" @click="counselingDialogVisible = false">取 消</el-button>
                    <el-button size="small" type="primary" :loading="submittingCounseling" @click="submitCounselingForm">保 存</el-button>
                </div>
            </el-dialog>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import PsychologyAttachmentViewer from '../../components/Psychology/PsychologyAttachmentViewer';
import { getPsychologyAppointments, exportPsychologyAppointments, savePsychologyCounselingRecord } from '../../api/modules/psychology';
import {
    resolveActiveSchoolId,
    normalizeDeptId,
    treeNodeDeptId,
    deptIdsEqual,
} from '../../utils/deptCascaderProps';

export default {
    name: 'PsychologyAppointment',
    components: { DepartmentPane, PsychologyAttachmentViewer },
    data() {
        return {
            schoolId: '',
            schoolName: '',
            departmentId: '',
            dateRange: [],
            filterForm: {
                status: null,
                keyword: ''
            },
            tableData: [],
            page: 1,
            pageSize: 10,
            total: 0,
            loading: false,
            exporting: false,

            // 详情
            detailDialogVisible: false,
            currentRecord: null,

            // 录入/编辑纪要
            counselingDialogVisible: false,
            counselingDialogTitle: '录入心理咨询纪要',
            submittingCounseling: false,
            counselingForm: {
                Id: 0,
                ArchiveId: 0,
                SchoolId: 0,
                CampusId: 0,
                StudentId: 0,
                StudentName: '',
                DepartmentName: '',
                AppointmentId: null,
                AppointDate: '',
                AppointmentReason: '',
                CounselingDate: '',
                TeacherName: '',
                AttentionLevel: null,
                Content: '',
                Remark: '',
                NextFollowUpDate: ''
            },
            counselingRules: {
                CounselingDate: [{ required: true, message: '请选择咨询开展时间', trigger: 'change' }],
                Content: [{ required: true, message: '请输入咨询纪要内容', trigger: 'blur' }]
            }
        };
    },
    computed: {
        hasSchoolSelected() {
            return !!normalizeDeptId(this.schoolId);
        },
    },
    watch: {
        '$store.state.activeSchoolId'(newSchoolId) {
            const id = normalizeDeptId(newSchoolId);
            if (id && !deptIdsEqual(id, this.schoolId)) {
                this.schoolId = id;
                this.departmentId = '';
                this.page = 1;
                this.loadTableData();
            }
        }
    },
    methods: {
        resolveSchoolId() {
            const fromStore = resolveActiveSchoolId(this);
            if (fromStore) return fromStore;
            return normalizeDeptId(this.schoolId);
        },

        treeNodeClick(data, node) {
            if (!data) return;
            const orgType = String((data.DeptOrgType || data.deptOrgType) || '').toLowerCase();
            const activeSchoolId = this.resolveSchoolId();
            const isSchoolNode = orgType === 'school' || orgType === 'affiliateschool'
                || (!orgType && (Number(data.type) === 1 || deptIdsEqual(data.value, activeSchoolId)));

            if (isSchoolNode) {
                this.schoolId = treeNodeDeptId(data) || activeSchoolId;
                this.schoolName = data.label || '';
                this.departmentId = '';
            } else {
                this.departmentId = treeNodeDeptId(data);
                this.schoolName = data.label || this.schoolName;
                let tempNode = node;
                let foundSchoolId = '';
                while (tempNode) {
                    const pType = String((tempNode.data && (tempNode.data.DeptOrgType || tempNode.data.deptOrgType)) || '').toLowerCase();
                    if (tempNode.data && (pType === 'school' || pType === 'affiliateschool')) {
                        foundSchoolId = treeNodeDeptId(tempNode.data);
                        break;
                    }
                    tempNode = tempNode.parent;
                }
                this.schoolId = foundSchoolId || activeSchoolId || normalizeDeptId(this.schoolId);
            }
            this.page = 1;
            this.loadTableData();
        },

        formatDate(val) {
            if (!val) return '-';
            const s = String(val);
            return s.length >= 10 ? s.substring(0, 10) : s;
        },

        getNowDateTimeString() {
            const d = new Date();
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },

        formatRecordTime(item) {
            if (!item) return '-';
            const cTime = item.CounselingDate ? String(item.CounselingDate).replace('T', ' ') : '';
            const createTime = item.CreateTime ? String(item.CreateTime).replace('T', ' ') : '';

            // 1. 若 CounselingDate 具有有效时分秒且不是 00:00:00，优先展示（时分：yyyy-MM-dd HH:mm）
            if (cTime && cTime.length >= 16 && !cTime.includes('00:00:00')) {
                return cTime.slice(0, 16);
            }
            // 2. 若 CounselingDate 只有日期（或者 00:00:00），提取 CreateTime 的具体时间点补充呈现
            if (createTime && createTime.length >= 16) {
                const datePart = cTime ? cTime.slice(0, 10) : createTime.slice(0, 10);
                const timePart = createTime.slice(11, 16);
                return `${datePart} ${timePart}`;
            }
            if (cTime) {
                return cTime.slice(0, 16);
            }
            return '-';
        },

        formatAttentionText(level) {
            const num = Number(level);
            switch (num) {
                case 0: return '正常';
                case 1: return '关注';
                case 2: return '重点关注';
                case 3: return '危机干预';
                default: return '正常';
            }
        },

        getAttentionTagType(level) {
            const num = Number(level);
            switch (num) {
                case 0: return 'info';
                case 1: return 'warning';
                case 2: return 'danger';
                case 3: return 'danger';
                default: return 'info';
            }
        },

        formatAppointmentStatusText(status) {
            const num = Number(status);
            switch (num) {
                case 0: return '待审核';
                case 1: return '已同意';
                case 2: return '已拒绝';
                case 3: return '已签到';
                case 4: return '已取消';
                default: return '已取消';
            }
        },

        getAppointmentStatusTagType(status) {
            const num = Number(status);
            switch (num) {
                case 0: return 'warning';
                case 1: return 'primary';
                case 2: return 'danger';
                case 3: return 'success';
                case 4: return 'info';
                default: return 'info';
            }
        },

        async loadTableData() {
            if (!this.schoolId) return;
            this.loading = true;
            try {
                const params = {
                    page: this.page,
                    intPageSize: this.pageSize,
                    schoolId: this.schoolId,
                    departmentId: this.departmentId || 0,
                    status: this.filterForm.status !== null ? this.filterForm.status : null,
                    startDate: this.dateRange && this.dateRange[0] ? this.dateRange[0] : null,
                    endDate: this.dateRange && this.dateRange[1] ? this.dateRange[1] : null,
                    keyword: this.filterForm.keyword || ''
                };
                const res = await getPsychologyAppointments(params);
                if (res.data && res.data.success) {
                    const resp = res.data.response;
                    this.tableData = resp.data || [];
                    this.total = resp.dataCount || 0;

                    // 若当前打开了详情弹窗，同步刷新当前对象
                    if (this.detailDialogVisible && this.currentRecord) {
                        const refreshed = this.tableData.find(item => String(item.Id) === String(this.currentRecord.Id));
                        if (refreshed) {
                            this.currentRecord = refreshed;
                        }
                    }
                } else {
                    this.tableData = [];
                    this.total = 0;
                }
            } catch (err) {
                this.$message.error('加载心理预约流水失败');
            } finally {
                this.loading = false;
            }
        },

        handleSearch() {
            this.page = 1;
            this.loadTableData();
        },

        handleReset() {
            this.filterForm.status = null;
            this.filterForm.keyword = '';
            this.dateRange = [];
            this.page = 1;
            this.loadTableData();
        },

        handleSizeChange(val) {
            this.pageSize = val;
            this.page = 1;
            this.loadTableData();
        },

        handlePageChange(val) {
            this.page = val;
            this.loadTableData();
        },

        viewDetail(row) {
            this.currentRecord = row;
            this.detailDialogVisible = true;
        },

        openAddCounseling(record) {
            if (!record) return;
            let defaultTeacherName = '心理咨询师';
            try {
                const user = window.localStorage.user ? JSON.parse(window.localStorage.user) : null;
                if (user && (user.uRealName || user.uLoginName)) {
                    defaultTeacherName = user.uRealName || user.uLoginName;
                }
            } catch (_) {}

            this.counselingForm = {
                Id: 0,
                ArchiveId: record.ArchiveId || 0,
                SchoolId: this.schoolId,
                CampusId: record.CampusId || 0,
                StudentId: record.StudentId,
                StudentName: record.StudentName || '',
                DepartmentName: record.DepartmentName || record.ClassName || '',
                AppointmentId: record.Id,
                AppointDate: record.AppointDate,
                AppointmentReason: record.Reason || record.ConsultReason || '',
                CounselingDate: this.getNowDateTimeString(),
                TeacherName: defaultTeacherName,
                AttentionLevel: null,
                Content: '',
                Remark: '',
                NextFollowUpDate: ''
            };
            this.counselingDialogVisible = true;
            this.$nextTick(() => {
                if (this.$refs.counselingForm) {
                    this.$refs.counselingForm.clearValidate();
                }
            });
        },

        submitCounselingForm() {
            this.$refs.counselingForm.validate(async (valid) => {
                if (!valid) return;
                this.submittingCounseling = true;
                try {
                    const res = await savePsychologyCounselingRecord(this.counselingForm);
                    if (res.data && res.data.success) {
                        this.$message.success('咨询纪要已保存');
                        this.counselingDialogVisible = false;

                        // 同步刷新当前详情视图中的纪要列表
                        if (this.currentRecord && String(this.currentRecord.Id) === String(this.counselingForm.AppointmentId)) {
                            if (!this.currentRecord.CounselingRecords) {
                                this.$set(this.currentRecord, 'CounselingRecords', []);
                            }
                            this.currentRecord.CounselingRecords.unshift({
                                Id: Date.now(),
                                TeacherName: this.counselingForm.TeacherName || '心理咨询师',
                                AttentionLevel: this.counselingForm.AttentionLevel,
                                CounselingDate: this.counselingForm.CounselingDate,
                                Content: this.counselingForm.Content,
                                NextFollowUpDate: this.counselingForm.NextFollowUpDate,
                                Remark: this.counselingForm.Remark,
                                Attachments: []
                            });
                            this.currentRecord.HasCounselingRecord = true;
                        }

                        // 刷新后台列表
                        this.loadTableData();
                    } else {
                        this.$message.error(res.data ? res.data.msg : '保存失败');
                    }
                } catch (err) {
                    this.$message.error('保存纪要异常');
                } finally {
                    this.submittingCounseling = false;
                }
            });
        },

        async handleExport() {
            if (!this.schoolId) return;
            this.exporting = true;
            try {
                const params = {
                    schoolId: this.schoolId,
                    departmentId: this.departmentId || 0,
                    status: this.filterForm.status !== null ? this.filterForm.status : null,
                    startDate: this.dateRange && this.dateRange[0] ? this.dateRange[0] : null,
                    endDate: this.dateRange && this.dateRange[1] ? this.dateRange[1] : null,
                    keyword: this.filterForm.keyword || ''
                };
                const res = await exportPsychologyAppointments(params);
                const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `心理咨询预约流水_${new Date().toISOString().slice(0, 10)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                this.$message.success('预约流水导出成功');
            } catch (err) {
                this.$message.error('导出 Excel 失败');
            } finally {
                this.exporting = false;
            }
        }
    },
    mounted() {
        const defaultSchoolId = this.resolveSchoolId();
        if (defaultSchoolId) {
            this.schoolId = defaultSchoolId;
        }
        // 列表由左侧教学树首次点击加载，避免与 DepartmentPane 初始化重复请求
    }
};
</script>

<style scoped>
.filter-bar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.filter-form .el-form-item {
    margin-bottom: 6px;
}

.student-name {
    font-weight: 600;
    color: #303133;
}

.text-muted {
    color: #c0c4cc;
}

.toolbar {
    margin-top: 8px;
    padding: 0;
    flex-shrink: 0;
}

.face-photo-box {
    margin-top: 16px;
    padding: 12px;
    background: #fafafa;
    border-radius: 6px;
}

.photo-title {
    font-size: 13px;
    color: #606266;
    margin-bottom: 8px;
}

.no-department-tip {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 200px;
    color: #909399;
}

.no-department-tip i {
    font-size: 48px;
    margin-bottom: 12px;
    color: #c0c4cc;
}

.appointment-detail {
    max-height: 72vh;
    overflow-y: auto;
    padding-right: 4px;
}

.appointment-counseling-box {
    margin-top: 14px;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    background: #fcfdfe;
    padding: 12px 14px;
}

.counseling-box-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px dashed #e4e7ed;
}

.c-header-left {
    display: flex;
    align-items: center;
}

.c-header-left i {
    color: #409eff;
    font-size: 15px;
    margin-right: 6px;
}

.box-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
}

.counseling-record-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.counseling-record-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 12px 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    border-left: 3px solid #6366f1;
}

.c-seq-badge {
    display: inline-block;
    padding: 1px 6px;
    background: #eef2ff;
    color: #4f46e5;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    margin-right: 6px;
    border: 1px solid #e0e7ff;
}

.c-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    color: #606266;
    margin-bottom: 6px;
}

.c-teacher {
    display: flex;
    align-items: center;
    font-weight: 500;
}

.c-teacher i {
    color: #409eff;
    margin-right: 4px;
}

.c-time {
    color: #909399;
}

.c-content {
    font-size: 13px;
    color: #303133;
    line-height: 1.6;
    background: #f8fafc;
    padding: 8px 10px;
    border-radius: 4px;
    white-space: pre-wrap;
    word-break: break-all;
}

.c-attachments {
    margin-top: 8px;
    display: flex;
    align-items: flex-start;
}

.att-title {
    font-size: 12px;
    color: #909399;
    margin-right: 8px;
    margin-top: 6px;
    flex-shrink: 0;
}

.c-footer {
    margin-top: 6px;
    font-size: 12px;
    color: #909399;
    display: flex;
    gap: 16px;
}

.follow-date i {
    margin-right: 3px;
}

.clickable-tag {
    cursor: pointer;
    transition: transform 0.15s ease;
}

.clickable-tag:hover {
    transform: scale(1.05);
}

.counseling-empty-action-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fdf6ec;
    border: 1px solid #faecd8;
    border-radius: 6px;
    padding: 12px 16px;
}

.empty-action-tip {
    display: flex;
    align-items: center;
    color: #b88230;
    font-size: 13px;
}

.empty-action-tip i {
    font-size: 18px;
    margin-right: 8px;
    color: #e6a23c;
}

.empty-action-tip strong {
    color: #cf7100;
    font-weight: 600;
}

.counseling-empty-tip {
    padding: 16px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 13px;
}

.counseling-empty-tip.is-muted {
    padding: 12px 0;
    color: #909399;
}

.counseling-empty-tip i {
    font-size: 18px;
    margin-right: 6px;
    color: #c0c4cc;
}

.appointment-detail ::v-deep .el-image-viewer__wrapper,
.face-photo-box ::v-deep .el-image-viewer__wrapper {
    z-index: 3000 !important;
}

.file-link-item {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    margin-right: 8px;
    padding: 3px 8px;
    background: #f4f4f5;
    border-radius: 4px;
    border: 1px solid #e9e9eb;
}
</style>
